/**
 * Run findings — how one run's findings split into problems and strengths.
 *
 * A strength (`helps`) is never open work: it gets no "Fix this", is not
 * counted as an open problem, and is listed apart from new problems in the
 * since-last-run delta. "Problem" is `isProblemType` from `@iamu/shared`,
 * the same predicate the lifecycle uses.
 */

import { isProblemType } from "@iamu/shared";

export function isStrength(type: string | null | undefined): boolean {
  return type === "helps";
}

/** Triage states a person set aside: not relevant, deferred, won't fix. */
export function isSetAside(triageStatus: string | null | undefined): boolean {
  return (
    triageStatus === "dismissed" ||
    triageStatus === "deferred" ||
    triageStatus === "wont_fix"
  );
}

export type FindingKind = "all" | "problems" | "strengths";

export function matchesKind(
  type: string | null | undefined,
  kind: FindingKind,
): boolean {
  if (kind === "problems") return isProblemType(type);
  if (kind === "strengths") return isStrength(type);
  return true;
}

export interface RunFindingCounts {
  openProblems: number;
  addressed: number;
  notRelevant: number;
  strengths: number;
}

export function countRunFindings(
  findings: ReadonlyArray<{ type: string | null; triageStatus: string }>,
): RunFindingCounts {
  const counts: RunFindingCounts = {
    openProblems: 0,
    addressed: 0,
    notRelevant: 0,
    strengths: 0,
  };
  for (const f of findings) {
    if (isSetAside(f.triageStatus)) {
      counts.notRelevant++;
    } else if (isStrength(f.type)) {
      counts.strengths++;
    } else if (isProblemType(f.type)) {
      if (f.triageStatus === "open") counts.openProblems++;
      else if (f.triageStatus === "addressed") counts.addressed++;
    }
  }
  return counts;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * The header summary, e.g. "2 open problems · 3 strengths". Open problems
 * always show; the rest only when there are any.
 */
export function describeRunFindingCounts(c: RunFindingCounts): string[] {
  const parts = [plural(c.openProblems, "open problem")];
  if (c.addressed > 0) parts.push(`${c.addressed} addressed`);
  if (c.notRelevant > 0) parts.push(`${c.notRelevant} not relevant`);
  if (c.strengths > 0) parts.push(plural(c.strengths, "strength"));
  return parts;
}

// ── Since last run ───────────────────────────────────────────────────

export interface DeltaItem {
  canonical_id?: string;
  id?: string;
  title: string;
  /** Finding type; absent in deltas from older CLIs. */
  type?: string;
}

export interface RunVerification {
  baseline_run_id: string;
  regressed?: DeltaItem[];
  resolved?: DeltaItem[];
  persists?: DeltaItem[];
  not_observed?: DeltaItem[];
  uncertain?: DeltaItem[];
  new_findings?: DeltaItem[];
}

export type DeltaGroupKey =
  | "regressed"
  | "new_problem"
  | "persists_problem"
  | "resolved"
  | "new_strength"
  | "persists_strength"
  | "not_observed"
  | "uncertain";

/**
 * Buckets of the CLI's since-last-run delta. Strengths — new or still
 * working — are good news, so they never share a bucket with problems.
 * An item without a type (older CLIs) is treated as a problem.
 */
export function splitRunVerification(
  v: RunVerification,
): Record<DeltaGroupKey, DeltaItem[]> {
  const persists = v.persists ?? [];
  const added = v.new_findings ?? [];
  return {
    regressed: v.regressed ?? [],
    new_problem: added.filter((i) => !isStrength(i.type)),
    persists_problem: persists.filter((i) => !isStrength(i.type)),
    resolved: v.resolved ?? [],
    new_strength: added.filter((i) => isStrength(i.type)),
    persists_strength: persists.filter((i) => isStrength(i.type)),
    not_observed: v.not_observed ?? [],
    uncertain: v.uncertain ?? [],
  };
}
